import s from './AppButton.module.scss';

/**
 * AppButton — bottone unificato del Design System.
 *
 * Quattro `mode`:
 *  - `nav`    — solid fill + lift on hover. Per azioni che PORTANO l'utente
 *              da qualche parte (apri modale, naviga a pagina).
 *  - `action` — pastel bg + bordo colorato. Per azioni che FANNO qualcosa
 *              (sync, export, add) nelle toolbar.
 *  - `ghost`  — trasparente (solo testo), hover con bg tenue. Per azioni a
 *              bassa enfasi: annulla / secondarie.
 *  - `icon`   — icon-only compatto, opacity 0.8 → 1 a hover. Per row action.
 *
 * `variant`: famiglia colore (primary | teal | success | danger | warning | info).
 *             Per icon mode sono accettate le stesse 6.
 * `size`: sm | md (default) | lg.
 *
 * I colori vengono dai design tokens (`src/app/styles/tokens.css`) — nessun
 * hex inline. Il componente è puro CSS + un piccolo spinner keyframes
 * (rimosso MUI Box/CircularProgress).
 *
 * Backward-compat: se qualcuno passa `sx`, lo applichiamo come `style` inline
 * (i 100+ consumer legacy lo usano per fine-tune occasionali).
 */
export default function AppButton({
  mode = 'action',
  variant = 'primary',
  size = 'md',
  icon,
  loading = false,
  disabled = false,
  onClick,
  title,
  type = 'button',
  children,
  sx,
  className,
  // A11y state for toggle buttons (aria-pressed) and disclosures
  // (aria-expanded + aria-controls). Omit for plain buttons.
  ariaPressed,
  ariaExpanded,
  ariaControls,
}) {
  const isDisabled = disabled || loading;

  const cap = (str) => str.charAt(0).toUpperCase() + str.slice(1);
  const variantClass = s[`${mode}${cap(variant)}`] || s[`${mode}Primary`];
  const sizeClass =
    mode === 'icon'
      ? s[`icon${cap(size === 'md' ? 'Md' : size)}`]
      : s[`size${cap(size === 'md' ? 'Md' : size)}`];
  // Note: Md handling — the SCSS exports sizeSm/sizeMd/sizeLg and iconSm/iconMd/iconLg.
  const finalSizeClass = mode === 'icon' ? s[`icon${cap(size)}`] : s[`size${cap(size)}`];

  // Bottone solo-icona in modalità nav/action → quadrato (icon mode è già tale).
  const iconOnly =
    !!icon &&
    (children === undefined || children === null || children === false || children === '');
  const squareClass = iconOnly && mode !== 'icon' ? s.iconSquare : null;

  const classes = [
    s.button,
    s[mode],
    variantClass,
    finalSizeClass,
    squareClass,
    loading ? s.loading : null,
    className,
  ]
    .filter(Boolean)
    .join(' ');

  // Icon-only buttons have no visible text → need an accessible name.
  // Fall back to `title`, or a string child, so screen readers announce them.
  const ariaLabel =
    mode === 'icon' ? title || (typeof children === 'string' ? children : undefined) : undefined;

  return (
    <button
      type={type}
      className={classes}
      onClick={onClick}
      disabled={isDisabled}
      title={title}
      aria-label={ariaLabel}
      aria-pressed={ariaPressed}
      aria-expanded={ariaExpanded}
      aria-controls={ariaControls}
      style={sx}
    >
      {loading ? (
        <span className={s.spinner} aria-hidden="true" />
      ) : icon ? (
        <i className={icon} />
      ) : null}
      {mode !== 'icon' ? children : null}
    </button>
  );
}
